/**
 * Table definitions section. Schema-connection state is inlined on every row: a derived
 * `dataset.tableName` subline, a connection badge (in-schema / orphan), and an expansion revealing
 * the field defs whose `body.perTable` references this table. "Add field def" inside the expansion
 * opens the Field defs create sheet — there is no silent-stub flow. History: SIM-1502, SIM-1678.
 */
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  EmptyState,
  Input,
  Label,
  QueryView,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@simplicity/ui";
import { useMemo, useState } from "react";
import { notifyMutationError, notifyWarnings } from "./notify";
import { RevisionViewer } from "./RevisionViewer";
import {
  associatedFieldsForTable,
  buildSchemaIndex,
  findTableMatches,
  listUnmappedTables,
  type AssociatedField,
  type QualifiedTableRef,
  type SchemaIndex,
} from "./schemaConnection";
import type { AgentFieldDefinition, AgentTableDefinition, WarehouseSchema } from "./types";
import {
  useCreateTableDef,
  useDeleteTableDef,
  useFieldDefs,
  useTableDefs,
  useUpdateTableDef,
  useWarehouseSchema,
} from "./useDottieContext";

interface FormState {
  tableName: string;
  description: string;
  reason: string;
}

const EMPTY_FORM: FormState = { tableName: "", description: "", reason: "" };

function formStateWithTable(tableName: string): FormState {
  return { tableName, description: "", reason: "" };
}

function toFormState(td: AgentTableDefinition): FormState {
  return { tableName: td.tableName, description: td.description, reason: "" };
}

function TableDefEditorSheet({
  open,
  onOpenChange,
  initial,
  title,
  submitLabel,
  onSubmit,
  pending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial: FormState;
  title: string;
  submitLabel: string;
  onSubmit: (form: FormState) => void;
  pending: boolean;
}) {
  const [form, setForm] = useState<FormState>(initial);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>
            Table definitions describe a tenant's warehouse table to Dottie so she can answer
            questions accurately about it.
          </SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-4">
          <div className="space-y-1">
            <Label htmlFor="td-name">Table name</Label>
            <Input
              id="td-name"
              // eslint-disable-next-line jsx-a11y/no-autofocus -- SIM-1680: sheet content is dialog-equivalent; AT pattern is to focus the first field on open.
              autoFocus
              value={form.tableName}
              onChange={(e) => setForm({ ...form, tableName: e.target.value })}
              placeholder="silver_attendance"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="td-description">Description</Label>
            <Textarea
              id="td-description"
              rows={8}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="td-reason">Reason (optional)</Label>
            <Input
              id="td-reason"
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
            />
          </div>
        </div>
        <SheetFooter className="mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={() => onSubmit(form)} disabled={pending || form.tableName.trim() === ""}>
            {submitLabel}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function DescriptionCell({ text }: { text: string }) {
  if (!text) return <td className="col-muted" />;
  return (
    <td className="col-muted max-w-[28rem]">
      <TooltipProvider delayDuration={400}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="block truncate">{text}</span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-md whitespace-normal">
            {text}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </td>
  );
}

function formatQualifiedTables(matches: QualifiedTableRef[]): string {
  return matches.map((m) => `${m.datasetId}.${m.tableName}`).join(", ");
}

function ConnectionBadge({ inSchema }: { inSchema: boolean }) {
  if (inSchema) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-md bg-success/10 px-2 py-0.5 text-xs text-success"
        data-testid="td-conn-in-schema"
        aria-label="In warehouse schema"
      >
        <span aria-hidden="true">✓</span> in schema
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 rounded-md bg-destructive/10 px-2 py-0.5 text-xs text-destructive"
      data-testid="td-conn-orphan"
      aria-label="Not in warehouse schema"
    >
      <span aria-hidden="true">⚠</span> not in warehouse
    </span>
  );
}

function TableNameCell({
  row,
  matches,
  expanded,
  onToggle,
}: {
  row: AgentTableDefinition;
  matches: QualifiedTableRef[];
  expanded: boolean;
  onToggle: () => void;
}) {
  const qualified = matches.length > 0 ? formatQualifiedTables(matches) : null;
  return (
    <td>
      <div className="flex items-start gap-2">
        <button
          type="button"
          onClick={onToggle}
          className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-muted/40"
          aria-label={
            expanded
              ? `Collapse associated fields for ${row.tableName}`
              : `Expand associated fields for ${row.tableName}`
          }
          aria-expanded={expanded}
        >
          <span aria-hidden="true">{expanded ? "▾" : "▸"}</span>
        </button>
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <span className="font-medium text-foreground">{row.tableName}</span>
            <ConnectionBadge inSchema={matches.length > 0} />
          </div>
          {qualified && (
            <span
              className="font-mono text-xs text-muted-foreground"
              data-testid={`td-qualified-${row.tableName}`}
            >
              {qualified}
            </span>
          )}
        </div>
      </div>
    </td>
  );
}

function AssociatedFieldRow({ field }: { field: AssociatedField }) {
  const inSchema = field.inSchemaAt !== null;
  return (
    <tr className="text-xs">
      <td className="px-2 py-1 font-mono text-foreground">{field.fieldName}</td>
      <td className="px-2 py-1 text-muted-foreground">
        {field.perTableDescription || (
          <span className="italic text-muted-foreground">(no per-table override)</span>
        )}
      </td>
      <td className="px-2 py-1 text-right">
        {inSchema ? (
          <span
            className="inline-flex items-center gap-1 text-xs text-success"
            data-testid={`td-assoc-conn-ok-${field.fieldName}`}
            aria-label="Column exists in this table"
          >
            <span aria-hidden="true">✓</span> in table
          </span>
        ) : (
          <span
            className="inline-flex items-center gap-1 text-xs text-destructive"
            data-testid={`td-assoc-conn-orphan-${field.fieldName}`}
            aria-label="Column not in this table"
          >
            <span aria-hidden="true">⚠</span> not in table
          </span>
        )}
      </td>
    </tr>
  );
}

function TableRowExpansion({
  row,
  associatedFields,
  onAddFieldForTable,
}: {
  row: AgentTableDefinition;
  associatedFields: AssociatedField[];
  onAddFieldForTable: (tableName: string) => void;
}) {
  return (
    <tr>
      <td
        colSpan={3}
        className="bg-muted/20 px-4 py-3"
        data-testid={`td-expansion-${row.tableName}`}
      >
        {associatedFields.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No field definitions reference this table yet.
          </p>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="text-xs text-muted-foreground">
                <th className="px-2 py-1">Field</th>
                <th className="px-2 py-1">Per-table description</th>
                <th className="px-2 py-1 text-right">Connection</th>
              </tr>
            </thead>
            <tbody>
              {associatedFields.map((f) => (
                <AssociatedFieldRow key={f.id} field={f} />
              ))}
            </tbody>
          </table>
        )}
        <div className="mt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onAddFieldForTable(row.tableName)}
            aria-label={`Add field definition for ${row.tableName}`}
          >
            Add field def
          </Button>
        </div>
      </td>
    </tr>
  );
}

function TableDefRowGroup({
  row,
  matches,
  expanded,
  onToggle,
  associatedFields,
  onEdit,
  onDelete,
  onHistory,
  onAddFieldForTable,
}: {
  row: AgentTableDefinition;
  matches: QualifiedTableRef[];
  expanded: boolean;
  onToggle: () => void;
  associatedFields: AssociatedField[];
  onEdit: (row: AgentTableDefinition) => void;
  onDelete: (row: AgentTableDefinition) => void;
  onHistory: (row: AgentTableDefinition) => void;
  onAddFieldForTable: (tableName: string) => void;
}) {
  return (
    <>
      <tr>
        <TableNameCell row={row} matches={matches} expanded={expanded} onToggle={onToggle} />
        <DescriptionCell text={row.description} />
        <td className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => onEdit(row)}>
            Edit
          </Button>
          <Button variant="outline" size="sm" onClick={() => onHistory(row)}>
            History
          </Button>
          <Button variant="destructive" size="sm" onClick={() => onDelete(row)}>
            Delete
          </Button>
        </td>
      </tr>
      {expanded && (
        <TableRowExpansion
          row={row}
          associatedFields={associatedFields}
          onAddFieldForTable={onAddFieldForTable}
        />
      )}
    </>
  );
}

/**
 * SIM-1679 — crosswalk from "warehouse has it" to "Dottie has it": every warehouse table without an
 * `AgentTableDefinition`. Default-open when the tenant has zero defs (active onboarding), closed
 * otherwise so it does not dominate later. Each row opens the create sheet pre-populated with the
 * unqualified table name.
 */
function UnmappedTablesBlock({
  rows,
  defaultOpen,
  onAdd,
}: {
  rows: ReturnType<typeof listUnmappedTables>;
  defaultOpen: boolean;
  onAdd: (tableName: string) => void;
}) {
  const [open, setOpen] = useState(defaultOpen);
  if (rows.length === 0) return null;
  return (
    <details
      className="rounded-md border border-border bg-muted/30 p-3"
      data-testid="td-unmapped-block"
      open={open}
    >
      <summary
        className="cursor-pointer text-sm font-medium text-foreground"
        onClick={(e) => {
          e.preventDefault();
          setOpen((prev) => !prev);
        }}
      >
        Warehouse tables without a definition ({rows.length})
      </summary>
      <p className="mt-2 text-xs text-muted-foreground">
        These tables exist in the warehouse but Dottie has no description for them. Add one to make
        them queryable.
      </p>
      <ul className="mt-3 space-y-1.5">
        {rows.map((r) => (
          <li
            key={r.tableName}
            className="flex items-center justify-between gap-3 rounded border border-border bg-background px-3 py-1.5"
            data-testid={`td-unmapped-row-${r.tableName}`}
          >
            <div className="flex flex-col gap-0.5">
              <span className="font-medium text-foreground">{r.tableName}</span>
              <span className="font-mono text-xs text-muted-foreground">
                {r.locations.map((l) => `${l.datasetId}.${l.tableName}`).join(", ")}
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onAdd(r.tableName)}
              data-testid={`td-unmapped-add-${r.tableName}`}
            >
              + Add definition
            </Button>
          </li>
        ))}
      </ul>
    </details>
  );
}

function TableDefsTable({
  rows,
  schemaIndex,
  t2Fields,
  expandedIds,
  onToggleExpand,
  onEdit,
  onDelete,
  onHistory,
  onAddFieldForTable,
}: {
  rows: AgentTableDefinition[];
  schemaIndex: SchemaIndex;
  t2Fields: AgentFieldDefinition[];
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
  onEdit: (row: AgentTableDefinition) => void;
  onDelete: (row: AgentTableDefinition) => void;
  onHistory: (row: AgentTableDefinition) => void;
  onAddFieldForTable: (tableName: string) => void;
}) {
  if (rows.length === 0) {
    return (
      <EmptyState
        title="No table definitions yet"
        description="Document the first warehouse table for this tenant."
      />
    );
  }
  return (
    <div className="admin-table-container">
      <table className="admin-table">
        <thead>
          <tr>
            <th>Table name</th>
            <th>Description</th>
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <TableDefRowGroup
              key={row.id}
              row={row}
              matches={findTableMatches(row.tableName, schemaIndex)}
              expanded={expandedIds.has(row.id)}
              onToggle={() => onToggleExpand(row.id)}
              associatedFields={associatedFieldsForTable(row.tableName, t2Fields, schemaIndex)}
              onEdit={onEdit}
              onDelete={onDelete}
              onHistory={onHistory}
              onAddFieldForTable={onAddFieldForTable}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function useTableDefMutations(
  tenantId: string,
  editTarget: AgentTableDefinition | null,
  deleteTarget: AgentTableDefinition | null,
  closeCreate: () => void,
  closeEdit: () => void,
  closeDelete: () => void,
) {
  const createTableDef = useCreateTableDef(tenantId);
  const updateTableDef = useUpdateTableDef(tenantId);
  const deleteTableDef = useDeleteTableDef(tenantId);

  const handleCreate = (form: FormState) =>
    createTableDef.mutate(
      {
        tenantId,
        tableName: form.tableName,
        description: form.description,
        reason: form.reason || undefined,
      },
      {
        onSuccess: (res) => {
          notifyWarnings(res.warnings);
          closeCreate();
        },
        onError: (err) => notifyMutationError("Create table definition", err),
      },
    );

  const handleUpdate = (form: FormState) => {
    if (!editTarget) return;
    updateTableDef.mutate(
      {
        id: editTarget.id,
        tableName: form.tableName,
        description: form.description,
        reason: form.reason || undefined,
      },
      {
        onSuccess: (res) => {
          notifyWarnings(res.warnings);
          closeEdit();
        },
        onError: (err) => notifyMutationError("Update table definition", err),
      },
    );
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteTableDef.mutate(
      { id: deleteTarget.id },
      {
        onSuccess: () => closeDelete(),
        onError: (err) => notifyMutationError("Delete table definition", err),
      },
    );
  };

  return {
    createTableDef,
    updateTableDef,
    deleteTableDef,
    handleCreate,
    handleUpdate,
    handleDelete,
  };
}

const EMPTY_SCHEMA: WarehouseSchema = {
  fetchedAt: "",
  tenantId: "",
  error: null,
  datasets: [],
};

interface TableSectionBodyProps {
  tableDefsQuery: ReturnType<typeof useTableDefs>;
  fieldDefsQuery: ReturnType<typeof useFieldDefs>;
  schemaIndex: SchemaIndex;
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
  createOpen: boolean;
  setCreateOpen: (open: boolean) => void;
  createInitial: FormState;
  /** Open the create sheet with the given initial form state. */
  onOpenCreate: (initial: FormState) => void;
  editTarget: AgentTableDefinition | null;
  setEditTarget: (row: AgentTableDefinition | null) => void;
  deleteTarget: AgentTableDefinition | null;
  setDeleteTarget: (row: AgentTableDefinition | null) => void;
  historyTarget: AgentTableDefinition | null;
  setHistoryTarget: (row: AgentTableDefinition | null) => void;
  onAddFieldForTable: (tableName: string) => void;
  handleCreate: (form: FormState) => void;
  handleUpdate: (form: FormState) => void;
  handleDelete: () => void;
  createPending: boolean;
  updatePending: boolean;
}

function TableDeleteDialog({
  target,
  setTarget,
  onConfirm,
}: {
  target: AgentTableDefinition | null;
  setTarget: (row: AgentTableDefinition | null) => void;
  onConfirm: () => void;
}) {
  return (
    <AlertDialog open={!!target} onOpenChange={(open) => !open && setTarget(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete table definition?</AlertDialogTitle>
          <AlertDialogDescription>
            This removes the row for this tenant. A revision is recorded so the prior content stays
            viewable in History.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function TableSectionBody({
  tableDefsQuery,
  fieldDefsQuery,
  schemaIndex,
  expandedIds,
  onToggleExpand,
  createOpen,
  setCreateOpen,
  createInitial,
  onOpenCreate,
  editTarget,
  setEditTarget,
  deleteTarget,
  setDeleteTarget,
  historyTarget,
  setHistoryTarget,
  onAddFieldForTable,
  handleCreate,
  handleUpdate,
  handleDelete,
  createPending,
  updatePending,
}: TableSectionBodyProps) {
  const unmapped = useMemo(
    () => listUnmappedTables(tableDefsQuery.data ?? [], schemaIndex),
    [tableDefsQuery.data, schemaIndex],
  );
  const hasNoDefs = (tableDefsQuery.data ?? []).length === 0;
  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">Table definitions</h2>
        <Button onClick={() => onOpenCreate(EMPTY_FORM)}>Add table</Button>
      </div>

      <UnmappedTablesBlock
        rows={unmapped}
        defaultOpen={hasNoDefs}
        onAdd={(tableName) => onOpenCreate(formStateWithTable(tableName))}
      />

      <QueryView
        query={tableDefsQuery}
        error={(err) => (
          <p className="text-sm text-destructive" role="alert">
            Failed to load table definitions: {err.message}
          </p>
        )}
      >
        {(rows) => (
          <TableDefsTable
            rows={rows}
            schemaIndex={schemaIndex}
            t2Fields={fieldDefsQuery.data ?? []}
            expandedIds={expandedIds}
            onToggleExpand={onToggleExpand}
            onEdit={setEditTarget}
            onDelete={setDeleteTarget}
            onHistory={setHistoryTarget}
            onAddFieldForTable={onAddFieldForTable}
          />
        )}
      </QueryView>

      <TableDefEditorSheet
        key={`create-${createInitial.tableName}-${String(createOpen)}`}
        open={createOpen}
        onOpenChange={setCreateOpen}
        initial={createInitial}
        title="New table definition"
        submitLabel="Create"
        onSubmit={handleCreate}
        pending={createPending}
      />

      <TableDefEditorSheet
        key={editTarget?.id ?? "edit-none"}
        open={!!editTarget}
        onOpenChange={(open) => !open && setEditTarget(null)}
        initial={editTarget ? toFormState(editTarget) : EMPTY_FORM}
        title="Edit table definition"
        submitLabel="Save"
        onSubmit={handleUpdate}
        pending={updatePending}
      />

      <TableDeleteDialog
        target={deleteTarget}
        setTarget={setDeleteTarget}
        onConfirm={handleDelete}
      />

      <RevisionViewer
        entityType="tableDefinition"
        id={historyTarget?.id ?? null}
        open={!!historyTarget}
        onOpenChange={(open) => !open && setHistoryTarget(null)}
        title={historyTarget ? `History — ${historyTarget.tableName}` : "History"}
      />
    </section>
  );
}

export function TableDefinitionsSection({
  tenantId,
  onAddFieldForTable,
}: {
  tenantId: string;
  onAddFieldForTable?: (tableName: string) => void;
}) {
  const tableDefsQuery = useTableDefs(tenantId);
  const fieldDefsQuery = useFieldDefs(tenantId);
  const warehouseQuery = useWarehouseSchema(tenantId);

  const [createOpen, setCreateOpen] = useState(false);
  const [createInitial, setCreateInitial] = useState<FormState>(EMPTY_FORM);
  const [editTarget, setEditTarget] = useState<AgentTableDefinition | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AgentTableDefinition | null>(null);
  const [historyTarget, setHistoryTarget] = useState<AgentTableDefinition | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());

  const handleOpenCreate = (initial: FormState) => {
    setCreateInitial(initial);
    setCreateOpen(true);
  };

  const { createTableDef, updateTableDef, handleCreate, handleUpdate, handleDelete } =
    useTableDefMutations(
      tenantId,
      editTarget,
      deleteTarget,
      () => setCreateOpen(false),
      () => setEditTarget(null),
      () => setDeleteTarget(null),
    );

  const schemaIndex = useMemo(
    () => buildSchemaIndex(warehouseQuery.data ?? EMPTY_SCHEMA),
    [warehouseQuery.data],
  );

  const handleToggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <TableSectionBody
      tableDefsQuery={tableDefsQuery}
      fieldDefsQuery={fieldDefsQuery}
      schemaIndex={schemaIndex}
      expandedIds={expandedIds}
      onToggleExpand={handleToggleExpand}
      createOpen={createOpen}
      setCreateOpen={setCreateOpen}
      createInitial={createInitial}
      onOpenCreate={handleOpenCreate}
      editTarget={editTarget}
      setEditTarget={setEditTarget}
      deleteTarget={deleteTarget}
      setDeleteTarget={setDeleteTarget}
      historyTarget={historyTarget}
      setHistoryTarget={setHistoryTarget}
      onAddFieldForTable={(tableName) => onAddFieldForTable?.(tableName)}
      handleCreate={handleCreate}
      handleUpdate={handleUpdate}
      handleDelete={handleDelete}
      createPending={createTableDef.isPending}
      updatePending={updateTableDef.isPending}
    />
  );
}
